import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert, Button, Drawer, Popconfirm, Space, Spin, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { IconDelete, IconEdit, IconReload } from './reportingIcons';
import CategoryLabelMapEditorDrawer from './CategoryLabelMapEditorDrawer';
import {
    listCategoryLabelMaps,
    ensureDefaultCategoryLabelMaps,
    deleteCategoryLabelMap,
} from '../../../actions/reportingActions';

const { Text } = Typography;

export default function CategoryLabelMapsDrawer({ open, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { items = [], loading } = useSelector(s => s.reportCategoryLabelMapList ?? { items: [] });

    const [seeding, setSeeding] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);

    useEffect(() => {
        if (open) dispatch(listCategoryLabelMaps());
    }, [open, dispatch]);

    const openCreate = () => {
        setEditingRow(null);
        setEditorOpen(true);
    };

    const openEdit = (row) => {
        setEditingRow(row);
        setEditorOpen(true);
    };

    const closeEditor = (saved) => {
        setEditorOpen(false);
        setEditingRow(null);
        if (saved) dispatch(listCategoryLabelMaps());
    };

    const handleSeedDefaults = async () => {
        setSeeding(true);
        const result = await dispatch(ensureDefaultCategoryLabelMaps());
        setSeeding(false);
        if (result?.error) {
            message.error(result.error);
            return;
        }
        const created = result?.created?.length ?? 0;
        if (created > 0) {
            message.success(t('Added {{count}} default label map(s)', { count: created }));
        } else {
            message.info(t('All default label maps are already present'));
        }
    };

    const handleDelete = async (id) => {
        const ok = await dispatch(deleteCategoryLabelMap(id));
        if (ok) message.success(t('Label map deleted'));
    };

    const columns = [
        {
            title: t('Dimension'),
            dataIndex: 'dimensionKey',
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{v}</Text>
                    {r.displayName && <Text type="secondary" style={{ fontSize: 12 }}>{r.displayName}</Text>}
                </Space>
            ),
        },
        {
            title: t('Mappings'),
            key: 'count',
            width: 100,
            render: (_, r) => <Tag>{Object.keys(r.mappings ?? {}).length}</Tag>,
        },
        {
            title: t('Enabled'),
            dataIndex: 'enabled',
            width: 90,
            render: v => (v ? <Tag color="green">{t('Yes')}</Tag> : <Tag>{t('No')}</Tag>),
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 120,
            render: (_, r) => (
                <Space>
                    <Button type="text" icon={<IconEdit />} onClick={() => openEdit(r)} />
                    <Popconfirm title={t('Delete this label map?')} onConfirm={() => handleDelete(r.id)}>
                        <Button type="text" danger icon={<IconDelete />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Drawer
                title={t('Category label maps')}
                open={open}
                onClose={onClose}
                width={640}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('How label maps work')}
                    description={t(
                        'Maps apply to every SQL reporting chart in your organisation. Each dimension key (e.g. sex, ethnicity, status) must match the chart’s dimension filter. Raw codes returned by report functions are shown as readable labels on charts, tables, and PDF exports. Queries are not changed — only the display layer.',
                    )}
                />

                <Space wrap style={{ marginBottom: 16 }}>
                    <Button icon={<IconReload />} onClick={() => dispatch(listCategoryLabelMaps())} loading={loading}>
                        {t('Refresh')}
                    </Button>
                    <Popconfirm
                        title={t('Load default label maps?')}
                        description={t('Adds missing dimensions from the app bundle. Your existing maps are not overwritten.')}
                        onConfirm={handleSeedDefaults}
                        okText={t('Load defaults')}
                        cancelText={t('Cancel')}
                    >
                        <Button loading={seeding}>
                            {t('Load defaults')}
                        </Button>
                    </Popconfirm>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        {t('Add dimension')}
                    </Button>
                </Space>

                <Spin spinning={loading}>
                    <Table
                        rowKey="id"
                        size="small"
                        dataSource={items}
                        columns={columns}
                        pagination={{ pageSize: 10, hideOnSinglePage: true }}
                    />
                </Spin>
            </Drawer>

            <CategoryLabelMapEditorDrawer
                open={editorOpen}
                editingRow={editingRow}
                onClose={closeEditor}
            />
        </>
    );
}
